import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import {
  shipmentsApi,
  QueryShipmentParams,
  CreateShipmentDto,
  UpdateStatusDto,
} from '../services/shipments.api';
import { toast } from 'sonner';
import { getErrorMessage } from '../client';

/**
 * Query hook for fetching paginated shipments list
 */
export function useShipmentsList(params: QueryShipmentParams = {}) {
  return useQuery({
    queryKey: queryKeys.shipments.list(params),
    queryFn: () => shipmentsApi.getAll(params),
  });
}

/**
 * Query hook for fetching a single shipment
 */
export function useShipment(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.shipments.detail(id || ''),
    queryFn: () => shipmentsApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching shipment by tracking number
 */
export function useShipmentByTracking(trackingNumber: string | undefined) {
  return useQuery({
    queryKey: queryKeys.shipments.tracking(trackingNumber || ''),
    queryFn: () => shipmentsApi.getByTracking(trackingNumber!),
    enabled: !!trackingNumber,
  });
}

/**
 * Query hook for fetching shipment milestones
 */
export function useShipmentMilestones(shipmentId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.shipments.milestones(shipmentId || ''),
    queryFn: () => shipmentsApi.getMilestones(shipmentId!),
    enabled: !!shipmentId,
  });
}

/**
 * Query hook for fetching shipment with linked purchase orders
 */
export function useShipmentWithOrders(id: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.shipments.detail(id || ''), 'withOrders'],
    queryFn: () => shipmentsApi.getShipmentWithOrders(id!),
    enabled: !!id,
  });
}

/**
 * Mutation hook for creating a new shipment
 */
export function useCreateShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateShipmentDto) => shipmentsApi.create(data),
    onSuccess: () => {
      toast.success('Shipment created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating a shipment
 */
export function useUpdateShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateShipmentDto> }) =>
      shipmentsApi.update(id, data),
    onSuccess: (_, variables) => {
      toast.success('Shipment updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.detail(variables.id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating shipment status
 */
export function useUpdateShipmentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStatusDto }) =>
      shipmentsApi.updateStatus(id, data),
    onSuccess: (_, variables) => {
      toast.success(`Shipment status updated to ${variables.data.status}`);
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.milestones(variables.id) });
      // Also invalidate inventory if delivered
      if (variables.data.status === 'DELIVERED') {
        queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for adding a milestone to a shipment
 */
export function useAddShipmentMilestone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      shipmentId,
      data,
    }: {
      shipmentId: string;
      data: { milestone: string; description?: string; location?: string; timestamp?: string };
    }) => shipmentsApi.addMilestone(shipmentId, data),
    onSuccess: (_, variables) => {
      toast.success('Milestone added successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.detail(variables.shipmentId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.shipments.milestones(variables.shipmentId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for deleting a shipment
 */
export function useDeleteShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => shipmentsApi.delete(id),
    onSuccess: () => {
      toast.success('Shipment deleted successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
